// src/components/Sidebar.js
import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from "../context/AuthContext";
import {
  FaTachometerAlt, FaChartBar, FaServer, FaBuilding, FaCogs, FaHeadset,
  FaChevronDown, FaChevronRight, FaFileInvoiceDollar, FaChartLine,
  FaFlag, FaUserShield, FaSlidersH, FaClipboardList, FaTicketAlt,
  FaArrowLeft, FaArrowRight, FaSignOutAlt, FaChevronLeft
} from 'react-icons/fa';
import './Sidebar.css';

/**
 * @file Sidebar.js
 * @description Main navigation sidebar with collapsible behavior and grouped menu items.
 */
const Sidebar = ({ collapsed, toggleFormatted, mobileOpen, closeMobile }) => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const { logout, user } = useAuth();

  // Close sidebar on route change (for mobile)
  React.useEffect(() => {
    if (closeMobile) closeMobile();
  }, [location.pathname]);

  // State for expanded groups
  const [expandedGroups, setExpandedGroups] = useState({
    analytics: true,
    settings: false,
    operations: false
  });

  const toggleGroup = (group) => {
    if (collapsed) return; // Don't toggle if collapsed
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  const menuGroups = [
    { type: 'link', to: '/dashboard', icon: FaTachometerAlt, label: 'Dashboard' },
    {
      type: 'group', id: 'analytics', label: 'Analytics', icon: FaChartBar,
      items: [
        { to: '/billing', label: 'Financials & Usage', icon: FaFileInvoiceDollar, disabled: false },
        { to: '/analytics', label: 'Usage Analytics', icon: FaChartLine }
      ]
    },
    { type: 'link', to: '/health', icon: FaServer, label: 'System Health' },
    { type: 'link', to: '/tenants', icon: FaBuilding, label: 'Tenant Management' },
    {
      type: 'group', id: 'operations', label: 'Operations & Support', icon: FaHeadset,
      items: [
        { to: '/audit', label: 'Audit Logs', icon: FaClipboardList },
        { to: '/support', label: 'Support / Tickets', icon: FaTicketAlt, disabled: false }
        // { to: '#', label: 'Support / Tickets', icon: FaTicketAlt, disabled: true }
      ]
    },
    { type: 'link', to: '/security', icon: FaCogs, label: 'Settings' }
  ];

  return (
    <div className={`sidebar d-flex flex-column flex-grow-1 transition-all ${mobileOpen ? 'show' : ''}`}
      style={{ width: collapsed ? '80px' : '260px', height: '100vh', position: 'fixed', zIndex: 1000, transition: 'all 0.3s ease', overflow: 'hidden' }}>

      {/* HEADER */}
      <div className={`p-4 border-bottom border-secondary d-flex align-items-center position-relative ${collapsed ? 'justify-content-center flex-column gap-3' : ''}`}>
        {!collapsed ? (
          <>
            <div style={{ width: '100%' }}>
              <h5 className="mb-0 fw-bold header-gradient text-nowrap" style={{ paddingBottom: "3px" }}>Super Admin Console</h5>
              <p style={{ color: "#E68F17", fontSize: "small", textAlign: "center" }}>WiiZ Atlas 2.0</p>
            </div>
            <button
              className="position-absolute d-none d-lg-flex align-items-center justify-content-center text-secondary cursor-pointer hover-text-white"
              style={{ right: '15px', bottom: '0%', transform: 'translateY(-50%)', width: '24px', height: '24px', borderRadius: '4px', background: 'rgba(255,255,255,0.05)' }}
              onClick={toggleFormatted}
              title="Collapse"
            >
              <FaChevronLeft size={12} />
            </button>
          </>
        ) : (
          <>
            <div className="fw-bold h5 mb-0 mx-auto">W</div>
            <button
              className="d-none d-lg-flex align-items-center justify-content-center text-secondary cursor-pointer hover-text-white"
              onClick={toggleFormatted}
              title="Expand"
              style={{ width: '24px', height: '24px', borderRadius: '4px', background: 'rgba(255,255,255,0.05)' }}
            >
              <FaChevronRight size={12} />
            </button>
          </>
        )}
      </div>

      {/* NAV */}
      <nav
        // className="nav flex-column flex-grow-1 flex-wrap p-3 custom-scrollbar "
        style={{
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
          width: '100%',
          height: "max-content",
          scrollbarColor: "rgb(108 117 125) transparent",
        }}
      >

        {menuGroups.map((item, index) => {
          if (item.type === 'link') {
            return (
              <NavLink
                key={index}
                to={item.to}
                className={({ isActive }) =>
                  `nav-link d-flex align-items-center mb-2 rounded transition-colors ${isActive ? "active-nav-item shadow-sm" : "text-secondary hover-bg-dark-light"}`
                }
                title={collapsed ? item.label : ""}
                style={{ justifyContent: collapsed ? 'center' : 'flex-start', padding: '12px' }}
              >
                <item.icon size={20} className={!collapsed ? "me-3" : ""} />
                {!collapsed && <span className="fw-medium">{item.label}</span>}
              </NavLink>
            );
          } else if (item.type === 'group') {
            const isExpanded = expandedGroups[item.id];
            const isActiveGroup = item.items.some(sub => sub.to === location.pathname);

            return (
              <div key={index} className="mb-2">
                <div
                  className={`nav-link d-flex align-items-center rounded cursor-pointer`}
                  style={{
                    justifyContent: collapsed ? 'center' : 'space-between',
                    padding: '12px',
                    maxWidth: '100%',
                    overflow: 'hidden'
                  }}
                  onClick={() => toggleGroup(item.id)}
                  title={collapsed ? item.label : ""}
                >
                  <div className="d-flex align-items-center">
                    <item.icon size={20} className={!collapsed ? "me-3" : ""} />
                    {!collapsed && <span className="fw-medium">{item.label}</span>}
                  </div>
                  {!collapsed && (isExpanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />)}
                </div>

                {/* Submenu - Only show if not collapsed */}
                {!collapsed && isExpanded && (
                  <div className="ms-4 mt-1 border-start border-secondary ps-2 animate-slide-down">
                    {item.items.map((subItem, subIndex) => (
                      <NavLink
                        key={subIndex}
                        to={subItem.to}
                        onClick={(e) => subItem.disabled && e.preventDefault()}
                        className={({ isActive }) =>
                          `nav-link d-flex align-items-center py-2 mb-1 rounded small ${subItem.disabled ? "text-secondary" : (isActive ? "text-white fw-bold" : "text-secondary hover-text-white")}`
                        }
                        style={subItem.disabled ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
                      >
                        <span className="me-2">•</span>
                        {subItem.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          }
          return null;
        })}
      </nav>

      {/* FOOTER & TOGGLE */}
      <div className="sidebar-footer p-3 border-top border-secondary">
        {/* Collapse Button (Now at top of footer) */}
        {/* User Info & Collapse Toggle */}
        {!collapsed && (
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center gap-2 text-white">
              <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 32, height: 32, backgroundColor: "#912059" }}>
                {(user?.full_name || user?.username || "A").charAt(0).toUpperCase()}
              </div>
              <div className="d-flex flex-column small">
                <span className="fw-bold">{user?.full_name || user?.username || "Admin"}</span>
                <span className="text-primaryd" style={{ fontSize: '0.7em' }}>{user?.role ? user.role.replace('_', ' ') : "Super Admin"}</span>
              </div>
            </div>
          </div>
        )}

        {/* Logout Button (Disabled per request) */}
        <button
          className={`btn r w-100 d-flex align-items-center justify-content-center gap-2 ${collapsed ? "p-2" : ""}`}
          style={{ backgroundColor: "#E68F17", }}
          onClick={logout}
          title="Sign Out"
        >
          <FaSignOutAlt />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;